import React, { useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { EnrollTwoFactor } from "@/components/EnrollTwoFactor";
import { Heart, Loader2, ShieldCheck, KeyRound } from "lucide-react";

const routeFor = (role) => (["super_admin", "employee"].includes(role) ? "/admin" : role === "volunteer" ? "/volunteer" : "/account");

export default function Login() {
  const { user, login, verify2fa } = useAuth();
  const [params] = useSearchParams();
  const portal = params.get("portal") || "donor"; // donor | super-admin | employee | volunteer (cosmetic only)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("password"); // password | twofa | enroll
  const [challengeToken, setChallengeToken] = useState("");
  const [enrollToken, setEnrollToken] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user && step !== "enroll") return <Navigate to={routeFor(user.role)} replace />;

  const titles = {
    donor: { h: "Donor Sign In", p: "Access your giving profile and impact updates." },
    "super-admin": { h: "Super Admin Access", p: "Secure operations login. Two-factor authentication required." },
    employee: { h: "Employee Login", p: "Refugium Ethiopia team access." },
    volunteer: { h: "Volunteer Login", p: "Volunteer workspace access." },
  };
  const t = titles[portal] || titles.donor;

  const submitPassword = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const d = await login(email, password);
      if (d.status === "2fa_required") { setChallengeToken(d.challenge_token); setStep("twofa"); }
      else if (d.status === "enrollment_required") { setEnrollToken(d.enrollment_token); setStep("enroll"); }
      else if (d.status === "ok") nav(routeFor(d.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const payload = { challenge_token: challengeToken };
      if (useRecovery) payload.recovery_code = code; else payload.code = code;
      const d = await verify2fa(payload);
      if (d.status === "ok") nav(routeFor(d.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="w-10 h-10 rounded-full bg-[#C1272D] flex items-center justify-center text-white"><Heart size={20} fill="white" /></span>
          <span className="font-display text-2xl font-bold text-[#1F4D2E]">Refugium Ethiopia</span>
        </Link>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8">
          {step === "enroll" ? (
            <EnrollTwoFactor enrollToken={enrollToken} onComplete={() => nav(routeFor(user?.role || "super_admin"))} />
          ) : step === "twofa" ? (
            <div data-testid="twofa-step">
              <div className="flex items-center gap-2 text-[#1F4D2E] mb-1"><ShieldCheck size={22} /><h1 className="font-admin text-2xl font-bold">Two-factor verification</h1></div>
              <p className="text-sm text-[#52525B] mb-6">{useRecovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
              {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="twofa-error">{error}</div>}
              <form onSubmit={submit2fa}>
                <input required value={code} onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ""))}
                  maxLength={useRecovery ? 40 : 6} inputMode={useRecovery ? "text" : "numeric"} data-testid="twofa-code"
                  className={`w-full border border-gray-200 rounded-md px-4 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] ${useRecovery ? "font-mono" : "tracking-[0.4em] text-center"}`}
                  placeholder={useRecovery ? "xxxxxxxx-xxxxxxxx-xxxxxxxx" : "000000"} />
                <button disabled={busy || !code} data-testid="twofa-submit" className="w-full bg-[#0A0A0A] text-white rounded-md py-3 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={18} /> : "Verify"}</button>
              </form>
              <button onClick={() => { setUseRecovery(!useRecovery); setCode(""); }} className="mt-4 text-sm text-[#C1272D] hover:underline inline-flex items-center gap-1.5" data-testid="toggle-recovery"><KeyRound size={14} /> {useRecovery ? "Use authenticator code instead" : "Use a recovery code"}</button>
            </div>
          ) : (
            <>
              <h1 className="font-admin text-2xl font-bold text-[#0A0A0A] mb-1">{t.h}</h1>
              <p className="text-sm text-[#52525B] mb-6">{t.p}</p>
              {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="login-error">{error}</div>}
              <form onSubmit={submitPassword} className="font-admin">
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]" placeholder="you@email.com" />
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Password</label>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 mb-2 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]" placeholder="••••••••" />
                <div className="flex justify-end mb-5">
                  <Link to="/forgot-password" className="text-sm text-[#C1272D] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <button disabled={busy} data-testid="login-submit" className="w-full bg-[#0A0A0A] text-white rounded-md px-4 py-3 font-medium hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}</button>
              </form>
              {portal === "donor" && <p className="text-sm text-[#52525B] mt-6 text-center">New here? <Link to="/signup" className="text-[#C1272D] font-semibold hover:underline" data-testid="to-signup">Create a donor account</Link></p>}
              {portal === "volunteer" && <p className="text-sm text-[#52525B] mt-6 text-center">Want to help? <Link to="/volunteer/apply" className="text-[#C1272D] font-semibold hover:underline" data-testid="to-volunteer-apply">Apply to volunteer</Link></p>}
            </>
          )}
        </div>
        <Link to="/" className="block text-center text-sm text-[#52525B] mt-6 hover:text-[#0A0A0A]">← Back to site</Link>
      </div>
    </div>
  );
}
